import type { MapMarkerCircleStyle } from '@gpx-spa/geo-domain'
import { ensureCirclePulseStylesInjected } from './ensure-circle-pulse-styles.js'

const DEFAULT_DOT_FILL = '#3b82f6'
const DEFAULT_DOT_BORDER = '#ffffff'
const DEFAULT_LABELED_FILL = '#ffffff'
const DEFAULT_LABELED_BORDER = '#374151'

/** Extra space around the dot so the pulse ring is not clipped (Leaflet divIcon / Mapbox Marker). */
const PULSE_MARGIN_PX = 12

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

export function circleMarkerHasLabel(style: MapMarkerCircleStyle): boolean {
  return !!(style.label && String(style.label).trim().length > 0)
}

/** Resolved pixel layout for both plain dots and labeled circles. */
export function resolveCircleMarkerLayout(style: MapMarkerCircleStyle): {
  size: number
  borderWidthPx: number
  fill: string
  border: string
  text: string
  fontSizePx: number
  shadow: string
} {
  const labeled = circleMarkerHasLabel(style)
  const size = style.sizePx ?? (labeled ? 28 : 22)
  if (labeled) {
    const border = style.borderColor ?? DEFAULT_LABELED_BORDER
    const fill = style.fillColor ?? DEFAULT_LABELED_FILL
    const text = style.textColor ?? border
    return {
      size,
      borderWidthPx: 2.5,
      fill,
      border,
      text,
      fontSizePx: style.fontSizePx ?? 12,
      shadow: '0 2px 4px rgba(0,0,0,.2)',
    }
  }
  const fill = style.fillColor ?? DEFAULT_DOT_FILL
  const border = style.borderColor ?? DEFAULT_DOT_BORDER
  return {
    size,
    borderWidthPx: 3,
    fill,
    border,
    text: '',
    fontSizePx: 12,
    shadow: '0 2px 8px rgba(0,0,0,.35)',
  }
}

/** Icon pixel size: larger when {@link MapMarkerCircleStyle.pulse} so the ripple fits inside the divIcon / marker element. */
export function resolveCircleMarkerOuterSizePx(style: MapMarkerCircleStyle): number {
  const { size } = resolveCircleMarkerLayout(style)
  if (!style.pulse) return size
  return size + 2 * PULSE_MARGIN_PX
}

function buildInnerCircleDivHtml(style: MapMarkerCircleStyle): string {
  const { size, borderWidthPx, fill, border, text, fontSizePx, shadow } = resolveCircleMarkerLayout(style)
  const labeled = circleMarkerHasLabel(style)
  const pulseClass = style.pulse ? ' gpx-map-circle-marker--pulse' : ''
  const pulseVar = style.pulse ? `--gpx-pulse-fill:${fill};` : ''
  if (labeled) {
    const label = escapeHtml(style.label!.trim())
    return `<div class="gpx-map-circle-marker${pulseClass}" style="
      box-sizing:border-box;margin:0;padding:0;
      width:${size}px;height:${size}px;border-radius:50%;
      border:${borderWidthPx}px solid ${border};
      background:${fill};color:${text};
      display:flex;align-items:center;justify-content:center;
      font-size:${fontSizePx}px;font-weight:700;line-height:1;
      box-shadow:${shadow};
      ${pulseVar}
    ">${label}</div>`
  }
  return `<div class="gpx-map-circle-marker${pulseClass}" style="
    box-sizing:border-box;margin:0;padding:0;
    width:${size}px;height:${size}px;border-radius:50%;
    border:${borderWidthPx}px solid ${border};
    background:${fill};
    box-shadow:${shadow};
    ${pulseVar}
  "></div>`
}

/**
 * Full HTML for Leaflet `L.divIcon` / string path. When `style.pulse`, wraps the disc in a flex box
 * {@link resolveCircleMarkerOuterSizePx} pixels wide so the pulse animation is not clipped.
 */
export function circleMarkerDivHtml(style: MapMarkerCircleStyle): string {
  if (style.pulse) {
    ensureCirclePulseStylesInjected()
  }
  const inner = buildInnerCircleDivHtml(style)
  if (!style.pulse) {
    return inner
  }
  const outer = resolveCircleMarkerOuterSizePx(style)
  return `<div style="width:${outer}px;height:${outer}px;display:flex;align-items:center;justify-content:center;margin:0;padding:0;line-height:0;box-sizing:border-box">${inner}</div>`
}

/**
 * DOM for Mapbox `mapboxgl.Marker({ element })`.
 *
 * Leaflet uses `L.divIcon`: the **icon element** is an outer div (`leaflet-marker-icon` + size),
 * and `circleMarkerDivHtml` is only the **inner** markup. Mapbox attaches `mapboxgl-marker`,
 * `role="img"`, and `transform: translate(-50%,-50%)` to whatever element we pass — if that
 * element is the circle itself, layout/typography inside the circle can shift vs Leaflet.
 * Mirror Leaflet by wrapping the same inner HTML in a neutral outer box (size × size, no border).
 */
export function createCircleMarkerElement(style: MapMarkerCircleStyle): HTMLElement {
  if (style.pulse) {
    ensureCirclePulseStylesInjected()
  }
  const outerSize = resolveCircleMarkerOuterSizePx(style)
  const outer = document.createElement('div')
  if (style.pulse) {
    outer.style.cssText = [
      'box-sizing:border-box',
      'display:flex',
      'align-items:center',
      'justify-content:center',
      `width:${outerSize}px`,
      `height:${outerSize}px`,
      'margin:0',
      'padding:0',
      'line-height:0',
    ].join(';')
  } else {
    outer.style.cssText = [
      'box-sizing:border-box',
      'display:block',
      `width:${outerSize}px`,
      `height:${outerSize}px`,
      'margin:0',
      'padding:0',
      'line-height:0',
    ].join(';')
  }

  const temp = document.createElement('div')
  temp.innerHTML = buildInnerCircleDivHtml(style).trim()
  const inner = temp.firstElementChild
  if (!(inner instanceof HTMLElement)) {
    throw new Error('createCircleMarkerElement: expected root div from buildInnerCircleDivHtml')
  }
  inner.classList.add('gpx-map-circle-marker')
  outer.appendChild(inner)
  return outer
}

export function circleMarkerPopupOffset(style: MapMarkerCircleStyle): [number, number] {
  const outer = resolveCircleMarkerOuterSizePx(style)
  const y = Math.round(outer / 2 + 8)
  return [0, -y]
}
